import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Package, Eye, EyeOff } from "lucide-react";

const fmt = (c) => `$${((c ?? 0) / 100).toFixed(0)}`;

/**
 * Optional bundle manager on the coach dashboard.
 *
 * A bundle is (name, N sessions, total price). Coaches decide whether to
 * publish any at all — the coach profile only shows `is_active=true` items.
 * Bundles are OPTIONAL — clients can still book a single session directly.
 */
export default function BundleManager({ coachEmail }) {
  const [bundles, setBundles] = useState([]);
  const [limits, setLimits] = useState({ max_bundles: 5 });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", sessions_count: 5, total_price_cents: 40000 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/bundles`);
      setBundles(data.bundles || []);
      setLimits(data.limits || { max_bundles: 5 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load bundles.");
    } finally {
      setLoading(false);
    }
  }, [coachEmail]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Bundle needs a name.");
    setSavingId("new");
    try {
      await api.post(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/bundles`, {
        name: form.name.trim(),
        sessions_count: Number(form.sessions_count),
        total_price_cents: Number(form.total_price_cents),
      });
      toast.success("Bundle created.");
      setShowForm(false);
      setForm({ name: "", sessions_count: 5, total_price_cents: 40000 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create bundle.");
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (b) => {
    const next = !b.is_active;
    setSavingId(b.id);
    setBundles((cur) => cur.map((x) => (x.id === b.id ? { ...x, is_active: next } : x)));
    try {
      await api.patch(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/bundles/${b.id}`, { is_active: next });
    } catch (err) {
      setBundles((cur) => cur.map((x) => (x.id === b.id ? { ...x, is_active: !next } : x)));
      toast.error(err?.response?.data?.detail || "Couldn't update.");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this bundle?")) return;
    setBundles((cur) => cur.filter((x) => x.id !== id));
    try {
      await api.delete(`/api/coach/dashboard/${encodeURIComponent(coachEmail)}/bundles/${id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete.");
      load();
    }
  };

  const atCap = bundles.length >= (limits.max_bundles || 5);

  return (
    <section
      data-testid="bundle-manager"
      className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 md:p-8"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl md:text-2xl tracking-tight text-slate-900">Session bundles</h2>
          <p className="mt-1 text-[13.5px] text-slate-500 max-w-[52ch]">
            Optional. Offer clients prepaid packs at a discount — you'll be paid upfront,
            they'll redeem credits at booking time.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12.5px] text-slate-500">{bundles.length}/{limits.max_bundles || 5}</span>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            disabled={atCap}
            data-testid="bundle-new-btn"
            className="btn-primary h-10 px-4 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Plus className="w-4 h-4" /> {showForm ? "Cancel" : "New bundle"}
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={create} className="mt-6 rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-4 gap-3" data-testid="bundle-form">
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Name</label>
            <input
              type="text" value={form.name} maxLength={80}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. 5-session starter"
              data-testid="bundle-form-name"
              className="w-full mt-1 text-[14px] px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-slate-400"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Sessions</label>
            <input
              type="number" min={2} max={50} value={form.sessions_count}
              onChange={(e) => setForm({ ...form, sessions_count: e.target.value })}
              data-testid="bundle-form-sessions"
              className="w-full mt-1 text-[14px] px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-slate-400"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Total ($)</label>
            <input
              type="number" min={10} max={10000}
              value={Math.round((form.total_price_cents || 0) / 100)}
              onChange={(e) => setForm({ ...form, total_price_cents: Math.round(Number(e.target.value) * 100) })}
              data-testid="bundle-form-price"
              className="w-full mt-1 text-[14px] px-3 py-2 rounded-lg border border-slate-200 focus:outline-none focus:border-slate-400"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              ≈ {fmt(Math.round((form.total_price_cents || 0) / Math.max(Number(form.sessions_count) || 1, 1)))}/session
            </p>
          </div>
          <div className="sm:col-span-4">
            <button
              type="submit"
              disabled={savingId === "new"}
              data-testid="bundle-form-submit"
              className="btn-primary h-10 px-5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2"
            >
              {savingId === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create bundle
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="mt-8 flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : bundles.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500" data-testid="bundle-empty">
          <Package className="w-5 h-5 mx-auto mb-2 opacity-60" />
          No bundles yet. Clients will only see the single-session price.
        </div>
      ) : (
        <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bundle-list">
          {bundles.map((b) => (
            <li
              key={b.id}
              data-testid={`bundle-item-${b.id}`}
              className="rounded-2xl border border-slate-200 p-5 bg-slate-50 flex flex-col"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display text-lg text-slate-900">{b.name}</h3>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${b.is_active ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-700"}`}>
                  {b.is_active ? "Active" : "Paused"}
                </span>
              </div>
              <p className="mt-2 text-slate-600 text-[14px]">
                <b>{b.sessions_count}</b> sessions · <b>{fmt(b.total_price_cents)}</b>
                <span className="text-slate-500"> · {fmt(b.per_session_cents)}/session</span>
              </p>
              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => toggleActive(b)}
                  data-testid={`bundle-toggle-${b.id}`}
                  disabled={savingId === b.id}
                  className={`h-8 px-3 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 ${
                    b.is_active ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : "bg-slate-900 text-white hover:bg-slate-800"
                  }`}
                >
                  {b.is_active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {b.is_active ? "Pause" : "Activate"}
                </button>
                <button
                  type="button"
                  onClick={() => remove(b.id)}
                  data-testid={`bundle-delete-${b.id}`}
                  className="h-8 w-8 rounded-full inline-flex items-center justify-center text-rose-600 hover:bg-rose-50"
                  title="Delete"
                ><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
